import { useEffect, useState } from "react";
import axios from "../api/axios";
import { Link } from "react-router-dom";
import { getActiveRole } from "../utils/auth";

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [submittedProfiles, setSubmittedProfiles] = useState([]);
  const [activeTab, setActiveTab] = useState("submitted");
  const [selectedDepartments, setSelectedDepartments] = useState({});
  const token = localStorage.getItem("token");
  const role = getActiveRole();

  const headers = {
    Authorization: `Bearer ${token}`
  };

  const departments = [
    { id: 1, code: "FY", name: "Engineering Sciences and Humanities" },
    { id: 2, code: "CSE-DS", name: "Computer Science & Engineering (Data Science)" },
    { id: 3, code: "AI", name: "Artificial Intelligence" },
    { id: 4, code: "CS", name: "Computer Science & Engineering (Cyber Security)" },
    { id: 5, code: "CSBS", name: "Computer Science & Business Systems" },
    { id: 6, code: "CSE", name: "Computer Science & Engineering" },
    { id: 7, code: "IT", name: "Information Technology" },
    { id: 8, code: "EE", name: "Electrical Engineering" },
    { id: 9, code: "ETC", name: "Electronics & Telecommunication" },
    { id: 10, code: "ME", name: "Mechanical Engineering" },
    { id: 11, code: "CIVIL", name: "Civil Engineering" },
    { id: 12, code: "IIOT", name: "Industrial IoT" },
    { id: 13, code: "RAI", name: "Robotics & AI" },
    { id: 14, code: "CDC", name: "Career Development Cell" }
  ];

  const getDepartmentDisplay = (deptId, profileDept) => {
    if (deptId) {
      const dept = departments.find(d => d.id === parseInt(deptId, 10));
      if (dept) return `${dept.code} - ${dept.name}`;
    }
    return profileDept || "N/A";
  };

  useEffect(() => {
    loadSubmittedProfiles();
    if (role === "admin" || role === "hr" || role === "super_admin") {
      loadAllUsers();
    }
  }, []);

  const loadSubmittedProfiles = async () => {
    try {
      const res = await axios.get("/profile/admin/submitted-profiles", { headers });
      setSubmittedProfiles(res.data.profiles || []);
    } catch (err) {
      console.log("Error loading submitted profiles:", err);
    }
  };

  const loadAllUsers = async () => {
    try {
      let res;
      if (role === "admin" || role === "super_admin") {
        res = await axios.get("/profile/admin/all-users", { headers });
        // Legacy endpoint may return raw array while newer endpoints return { users: [] }.
        const normalizedUsers = Array.isArray(res.data) ? res.data : (res.data.users || []);
        setUsers(normalizedUsers);
      } else if (role === "hr") {
        // HR can use the same admin endpoint or a specific HR endpoint
        res = await axios.get("/hr/users", { headers });
        setUsers(res.data.users || []);
      }
    } catch (err) {
      console.log("Error loading users:", err);
      setUsers([]); // Set empty array on error to prevent filter issues
    }
  };

  const convertToFaculty = async (userId) => {
    const departmentId = selectedDepartments[userId];
    if (!departmentId) {
      alert("Please select a department first");
      return;
    }
    try {
      if (role === "admin") {
        await axios.post(
          "/profile/admin/convert-to-faculty",
          {
            user_id: userId,
            department_id: departmentId
          },
          { headers }
        );
      } else if (role === "hr" || role === "super_admin") {
        await axios.put(
          `/hr/users/${userId}/convert-to-faculty`,
          {
            department_id: departmentId
          },
          { headers }
        );
      }
      alert("User converted to Faculty successfully!");
      window.location.reload();
    } catch (err) {
      alert("Conversion failed: " + (err.response?.data?.message || err.message));
    }
  };

  const convertToGeneral = async (userId) => {
    try {
      if (role === "admin" || role === "super_admin") {
        // Admin can use HR endpoint
        await axios.put(`/hr/users/${userId}/convert-to-general`, {}, { headers });
      } else if (role === "hr") {
        await axios.put(`/hr/users/${userId}/convert-to-general`, {}, { headers });
      }
      alert("User converted to General successfully!");
      window.location.reload();
    } catch (err) {
      alert("Conversion failed: " + (err.response?.data?.message || err.message));
    }
  };

  const toggleModerator = async (userId, currentValue) => {
    try {
      await axios.put(
        `/hr/users/${userId}/moderator`,
        { is_moderator: !currentValue },
        { headers }
      );

      alert(`Moderator access ${currentValue ? "revoked" : "granted"} successfully!`);
      loadAllUsers();
    } catch (err) {
      alert("Moderator update failed: " + (err.response?.data?.message || err.message));
    }
  };

  const handleDepartmentChange = (userId, departmentId) => {
    setSelectedDepartments(prev => ({
      ...prev,
      [userId]: departmentId
    }));
  };

  const filteredUsers = activeTab === "all"
    ? users
    : users.filter(user => user.role === activeTab);

  return (
    <div className="material-surface-strong p-6 md:p-8">
      <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4 tracking-tight">
        {role === "hr" ? "HR Dashboard - Employee Profiles" : role === "super_admin" ? "Super Admin Panel" : "Admin Panel"}
      </h2>
      <p className="text-slate-600 dark:text-slate-400 mb-8">
        Professional user and profile management with role-aware actions.
      </p>

      {/* Tab Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          onClick={() => setActiveTab("submitted")}
          className={`px-5 py-2.5 rounded-full font-semibold transition-all shadow-sm ${
            activeTab === "submitted"
              ? "bg-blue-600 text-white shadow-md"
              : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          }`}
        >
          📋 Submitted Profiles ({submittedProfiles.length})
        </button>
        
        {(role === "admin" || role === "hr" || role === "super_admin") && (
          <>
            <button
              onClick={() => setActiveTab("all")}
              className={`px-5 py-2.5 rounded-full font-semibold transition-all shadow-sm ${
                activeTab === "all"
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              All Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab("general")}
              className={`px-5 py-2.5 rounded-full font-semibold transition-all shadow-sm ${
                activeTab === "general"
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              General Users
            </button>
            <button
              onClick={() => setActiveTab("faculty")}
              className={`px-5 py-2.5 rounded-full font-semibold transition-all shadow-sm ${
                activeTab === "faculty"
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              Faculty
            </button>
          </>
        )}
      </div>

      <div className="border-t border-slate-200 dark:border-slate-800 py-6"></div>

      {/* Submitted Profiles Tab */}
      {activeTab === "submitted" && (
        <div className="space-y-4">
          {submittedProfiles.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 text-center shadow-sm">
              <p className="text-slate-500 dark:text-slate-400 text-lg">No submitted profiles yet</p>
              <p className="text-slate-400 dark:text-slate-500 text-sm mt-2">Employee profiles will appear here once they submit their forms</p>
            </div>
          ) : (
            submittedProfiles.map((profile) => (
              <div key={profile.profile_id} className="material-surface p-5 hover:shadow-lg transition-all">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-2">
                      {profile.email}
                    </p>
                    <div className="flex gap-4 items-center flex-wrap">
                      {profile.display_profile_id && (
                        <p className="text-slate-600 dark:text-slate-400 text-sm">
                          <span className="font-medium">Profile ID:</span>
                          <span className="inline-block ml-2 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded font-mono text-xs font-semibold text-slate-800 dark:text-slate-200">{profile.display_profile_id}</span>
                        </p>
                      )}
                      <p className="text-slate-600 dark:text-slate-400 text-sm">
                        <span className="font-medium">Department:</span> {getDepartmentDisplay(profile.department_id, profile.profile_department)}
                      </p>
                      <p className="text-slate-600 dark:text-slate-400 text-sm">
                        <span className="font-medium">Role:</span> 
                        <span className="inline-block ml-2 px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-xs font-semibold">
                          {profile.role}
                        </span>
                      </p>
                      <p className="text-slate-600 dark:text-slate-400 text-sm">
                        <span className="font-medium">Last Updated:</span> {new Date(profile.last_updated_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Link
                      to={`/report?profileId=${profile.profile_id}`}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg transition-colors text-sm text-center"
                    >
                      📊 View Full Report
                    </Link>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* User Management Tabs (Admin/HR) */}
      {(role === "admin" || role === "hr" || role === "super_admin") && (activeTab === "all" || activeTab === "general" || activeTab === "faculty") && (
        <div className="space-y-4">
          {filteredUsers.map(user => (
            <div key={user.id} className="material-surface p-5">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <p className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-2">{user.email}</p>
                  <p className="text-slate-600 dark:text-slate-400 text-sm"><span className="font-medium">Department:</span> {getDepartmentDisplay(user.department_id, user.profile_department)}</p>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">
                    <span className="font-medium">Role:</span> 
                    <span className="inline-block ml-2 px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-xs font-semibold">
                      {user.role}
                    </span>
                    {(user.is_moderator || user.role === "super_admin") && (
                      <span className="inline-block ml-2 px-3 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full text-xs font-semibold">
                        moderator
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  {user.profile_id ? (
                    <Link
                      to={`/report?profileId=${user.profile_id}`}
                      className="text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 font-medium text-sm underline"
                    >
                      View Full Profile
                    </Link>
                  ) : (
                    <span className="text-slate-400 dark:text-slate-500 text-sm italic">
                      No profile created
                    </span>
                  )}
                  {user.role === "general" ? (
                    <div className="flex flex-col gap-2">
                      <select
                        value={selectedDepartments[user.id] || ""}
                        onChange={(e) => handleDepartmentChange(user.id, e.target.value)}
                        className="border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>
                            {dept.code} - {dept.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => convertToFaculty(user.id)}
                        className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-full transition-all text-sm shadow-sm hover:shadow-md"
                      >
                        Convert to Faculty
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => convertToGeneral(user.id)}
                      className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-4 py-2 rounded-full transition-all text-sm shadow-sm hover:shadow-md"
                    >
                      Convert to General
                    </button>
                  )}

                  {user.role !== "super_admin" && (
                    <button
                      onClick={() => toggleModerator(user.id, !!user.is_moderator)}
                      className={`font-semibold px-4 py-2 rounded-full transition-all text-sm shadow-sm hover:shadow-md ${
                        user.is_moderator
                          ? "bg-purple-100 hover:bg-purple-200 text-purple-800 dark:bg-purple-900 dark:hover:bg-purple-800 dark:text-purple-200"
                          : "bg-purple-600 hover:bg-purple-700 text-white"
                      }`}
                    >
                      {user.is_moderator ? "Remove Moderator" : "Make Moderator"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
